import React, {useState, useCallback} from 'react';
import moment from 'moment';

import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import ky from 'ky';
import {LocalizationProvider, DatePicker} from '@mui/x-date-pickers';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import BackspaceIcon from '@mui/icons-material/Backspace';

import PACSTableCharges from './table';
import dateISOTimezone from '../../../helpers/dateISOTimezone';

interface LambdaUsageApiResponse {
  [accountId: string]: {
    [lambdaName: string]: {
      count: number,
      totalDurationInMs: number,
    }
  }
}

const PACSCharges = () => {
  const [
    startDate,
    setStartDate,
  ] = useState<moment.Moment | null>(moment().startOf('month'));

  const [
    endDate,
    setEndDate,
  ] = useState<moment.Moment | null>(moment().endOf('month'));

  const [loading, setLoading] = useState(false);
  const [clientUsages, setClientUsages] = useState<PACSCharges.RowData[]>([]);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);

    const clients = await ky.get(
        `${process.env.REACT_APP_PACS_BACKEND_API_URL}/accounts`,
        {
          searchParams: {
            isDeleted: '1,0',
          },
          headers: {
            access_token: process.env.REACT_APP_PACS_BACKEND_API_ROOT_KEY,
          },
        },
    )
        .then(async (res) => {
          const data = await res.json() as Account.GetQueryResult[];

          return data;
        });

    const getClientUsage = async (
        client: Account.GetQueryResult,
    ):Promise<PACSCharges.RowData> => {
      const {
        id,
        name,
        legalName = '',
        notificationEmail = '',
        companyIdentity = '',
      } = client;

      let lambdaUsage = {} as LambdaUsageApiResponse;
      if (startDate && endDate) {
        lambdaUsage = await ky.get(
            `${process.env.REACT_APP_PACS_ELASTIC_URL}/lambdaUsages/reports/usage`,
            {
              searchParams: {
                startedAtStartDate: dateISOTimezone(startDate.toDate()) + '-03:00',
                startedAtEndDate: dateISOTimezone(endDate.toDate()) + '-03:00',
                accountId: id,
              },
              headers: {
                rootkey: process.env.REACT_APP_PACS_ELASTIC_API_ROOT_KEY,
              },
            },
        )
            .then(async (res) => {
              const data = await res.json() as LambdaUsageApiResponse;

              return data;
            });
      }
      const deletedAt = client?.deletedAt ? 'Inativo' : 'Ativo';
      const isForHuman = client?.isForHuman ? 'Humano' : 'Veterinário';
      const hasResultsAccess = client?.hasResultsAccess ? 'Ativo' : 'Inativo';

      return {
        id,
        name,
        legalName,
        deletedAt,
        notificationEmail,
        isForHuman,
        hasResultsAccess,
        companyIdentity,
        rowsLength: clients.length,
        lambdaUsage: lambdaUsage?.[id.toString()] ?? {},
      };
    };

    const clientUsagesPromises = [];

    for (const client of clients) {
      clientUsagesPromises
          .push(
              getClientUsage(client),
          );
    }

    await Promise
        .all(clientUsagesPromises)
        .then((clientUsages) => setClientUsages(clientUsages));

    setLoading(false);
  }, [endDate, startDate]);

  const handleSearch = async () => {
    if (
      startDate &&
      endDate
    ) {
      await fetchAccounts();
    }
  };

  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setClientUsages([]);
    setLoading(false);
  };

  return (
    <Grid
      container
      spacing={6}
      style={{
        padding: '1em 2em 2em 2em',
      }}
    >
      <Grid item xs={12}>
        <Typography variant="h4">
        Cobrança PACS
        </Typography>
      </Grid>
      <Grid item xs={3}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            label="Data inicial"
            format="DD/MM/YYYY"
            value={startDate}
            onChange={(newValue: moment.Moment | null) => {
              if (newValue) {
                newValue.set({
                  hour: 0,
                  minute: 0,
                  second: 0,
                  millisecond: 0,
                });
              }

              setStartDate(newValue);
            }}
            inputRef={(params: any) => <TextField {...params} />}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item xs={3}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            label="Data final"
            format="DD/MM/YYYY"
            value={endDate}
            onChange={(newValue: moment.Moment | null) => {
              if (newValue) {
                newValue.set({
                  hour: 23,
                  minute: 59,
                  second: 59,
                  millisecond: 999,
                });
              }

              setEndDate(newValue);
            }}
            inputRef={(params: any) => <TextField {...params} />}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item xs={3}>
        <IconButton
          onClick={() => handleSearch()}
          disabled={!startDate && !endDate}
          color='primary'
        >
          <SearchIcon/> Buscar
        </IconButton>
      </Grid>
      <Grid item xs={3}>
        <IconButton
          onClick={() => handleClearFilters()}
          color='primary'
        >
          <BackspaceIcon/> Limpar
        </IconButton>
      </Grid>
      <Grid
        item
        xs={12}
        style={{
          paddingBottom: '1em',
        }}
      >
        {
          loading &&
          <Grid
            container
            spacing={4}
            style={{
              justifyContent: 'center',
              alignContent: 'center',
              textAlign: 'center',
            }}
          >
            <Grid
              item
              xs={12}
            >
              <CircularProgress
                style={{
                  top: '50%',
                  right: '50%',
                  zIndex: 11,
                }}
              />
            </Grid>
            <Grid
              item
              xs={12}
            >
              Colhendo dados de todos os clientes...
            </Grid>
          </Grid>
        }
        {
          !loading &&
          clientUsages?.length > 0 &&
          <PACSTableCharges
            rows={clientUsages}
            fetchAccounts={fetchAccounts}
          />
        }
      </Grid>
    </Grid>

  );
};

export default PACSCharges;
